import { Alert, DatePicker, Form, Input, Modal, Select } from "antd";
import { useForm } from "antd/es/form/Form";
import moment from "moment/moment";
import K from "../../../../../utilities/constants";
import styles from "../../clientInvoices.module.scss";
import { disabledDatesPriorMonth } from "../../../../../utilities/dateUtility";
import { useFormDirty } from "../../../../../hooks/useFormDirty";
import { usePermission } from "../../../../../hooks/usePermission";

const { Option } = Select;

const isRevertingToDraft = (details, selectedStatus) =>
  selectedStatus === K.Invoice.Status.Draft &&
  details?.status !== K.Invoice.Status.Draft;

const statusDisable = (details, selectedStatus, isSuperAdmin) => {
  if (details?.status === K.Invoice.Status.Draft) {
    return [K.Invoice.Status.Paid, K.Invoice.Status["Not Paid"]].includes(
      selectedStatus,
    );
  }
  if (details?.status === K.Invoice.Status["Billed in QuickBooks"]) {
    return selectedStatus === K.Invoice.Status.Draft ? !isSuperAdmin : false;
  }
  return true;
};

const statusOptions = Object.keys(K.Invoice.Status).map((item) => ({
  label: item,
  value: K.Invoice.Status[item],
}));

const CommissionStatementsError = ({ message, statements }) => (
  <Alert
    type="error"
    showIcon
    message={
      <>
        {message}
        {statements.length > 0 && (
          <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
            {statements.map(({ recruiter, statementId }) => (
              <li key={statementId}>
                {recruiter} (#{statementId})
              </li>
            ))}
          </ul>
        )}
      </>
    }
    className="mb-2"
  />
);

export const EditInvoiceModal = ({
  invoiceDetails,
  loading,
  error,
  errorStatements = [],
  onFinish,
  open,
  closeModal,
}) => {
  const [form] = useForm();
  const { hasAccess } = usePermission();
  const isSuperAdmin = hasAccess([K.Permissions.SuperAdmin]);

  const initialValues = {
    invoiceNumber: invoiceDetails?.invoiceNumber ?? "",
    status: invoiceDetails?.status ?? K.Invoice.Status.Draft,
    startDate: invoiceDetails?.dueDate ? moment(invoiceDetails?.dueDate) : null,
  };

  const [isDirty, onValuesChange] = useFormDirty(form, initialValues);

  const handleValuesChange = (changedValues, allValues) => {
    if (
      "status" in changedValues &&
      isRevertingToDraft(invoiceDetails, changedValues.status)
    ) {
      form.setFieldValue("invoiceNumber", null);
      onValuesChange({ ...allValues, invoiceNumber: null });
    } else {
      onValuesChange(allValues);
    }
  };

  return (
    <Modal
      className="s2-theme-style"
      centered
      title="Edit Invoice"
      open={open}
      closeIcon={<i className="icon-closeable" />}
      okButtonProps={{ disabled: !isDirty, loading }}
      onOk={form.submit}
      onCancel={closeModal}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
        onFinish={onFinish}
      >
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, curValues) =>
            prevValues.status !== curValues.status
          }
        >
          {({ getFieldValue }) => (
            <Form.Item
              name="invoiceNumber"
              label="Invoice Number"
              rules={
                !isRevertingToDraft(invoiceDetails, getFieldValue("status")) &&
                (invoiceDetails?.invoiceNumber ||
                  getFieldValue("status") ===
                    K.Invoice.Status["Billed in QuickBooks"])
                  ? [
                      {
                        required: true,
                        message: "Invoice number is required",
                      },
                    ]
                  : []
              }
            >
              <Input
                placeholder="Invoice Number"
                disabled={isRevertingToDraft(
                  invoiceDetails,
                  getFieldValue("status"),
                )}
              />
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item
          name="status"
          label="Invoice Status"
          rules={[{ required: true, message: "Invoice status is required" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Status"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          >
            {statusOptions.map(({ label, value }) => (
              <Option
                key={value}
                value={value}
                disabled={statusDisable(invoiceDetails, value, isSuperAdmin)}
              >
                {label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, curValues) =>
            prevValues.status !== curValues.status
          }
        >
          {({ getFieldValue }) =>
            isRevertingToDraft(invoiceDetails, getFieldValue("status")) && (
              <Alert
                type="warning"
                showIcon
                message="Reverting to Draft will remove all related commission approvals."
                className="mb-2"
              />
            )
          }
        </Form.Item>

        {error && (
          <CommissionStatementsError
            message={error}
            statements={errorStatements}
          />
        )}

        <Form.Item
          name="startDate"
          label="Invoice Date"
          rules={[{ required: true, message: "Invoice Date is required" }]}
        >
          <DatePicker
            placeholder="Invoice Date"
            allowClear={false}
            className={styles.datePicker}
            format={K.DateFormat.DashUSFormat}
            disabledDate={disabledDatesPriorMonth}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
